import { Box, Stack, Tab, Tabs } from '@mui/material';
import { ReactNode, useState } from 'react';
import * as React from 'react';

import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { COLORS } from '@/styles/tokens/colors';

export type TrayTab = 'Notifications' | 'Activities';

export interface TrayShellProps {
  onClose: () => void;
  defaultTab?: TrayTab;
  activeTab?: TrayTab;
  onTabChange?: (tab: TrayTab) => void;
  children: ReactNode;
}

export function TrayShell({
  onClose,
  defaultTab = 'Notifications',
  activeTab: controlledActiveTab,
  onTabChange,
  children,
}: TrayShellProps) {
  const [internalActiveTab, setInternalActiveTab] =
    useState<TrayTab>(defaultTab);

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabChange = (_: React.SyntheticEvent, newValue: TrayTab) => {
    if (isControlled) {
      onTabChange?.(newValue);
    } else {
      setInternalActiveTab(newValue);
    }
  };

  return (
    <Stack
      sx={{
        width: 450,
        height: '100%',
        backgroundColor: 'white',
        borderLeft: `1px solid ${COLORS.GRAY[200]}`,
      }}
    >
      {/* Header with Tabs and Close Button */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
          minHeight: 60,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            flexGrow: 1,
            height: 60,
            '& .MuiTab-root': {
              minHeight: 60,
              color: COLORS.TEXT.SECONDARY,
            },
            '& .MuiTab-root.Mui-selected': {
              color: COLORS.TEXT.PRIMARY,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: COLORS.ORANGE[500],
            },
          }}
        >
          <Tab label="Notifications" value="Notifications" />
          <Tab label="Activities" value="Activities" />
        </Tabs>
        <Box sx={{ pr: 1.5 }}>
          <CloseButton size="sm" variant="transparent" onClick={onClose} />
        </Box>
      </Stack>

      {/* Tab Content */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}
